import { useEffect, useState } from "react";
import { X, Loader2, Lock, User as UserIcon, ShieldCheck, Palette, Check } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { ROLE_LABELS } from "../lib/permissions";
import { toast } from "sonner";

/** Build Prompt #15 — User Settings modal triggered from the sidebar avatar.
 *  Tabs: Profile | Security | Credentials. All modals are Snow-on-Jet high-contrast.
 */
export const AVATAR_PALETTE = [
    { id: "signal", label: "Signal", hex: "#FF6A00" },
    { id: "snow", label: "Snow", hex: "#F5F5F5" },
    { id: "cyan", label: "Cyan", hex: "#22D3EE" },
    { id: "magenta", label: "Magenta", hex: "#E879F9" },
    { id: "lime", label: "Lime", hex: "#A3E635" },
    { id: "cobalt", label: "Cobalt", hex: "#3B82F6" },
];
export const avatarHex = (id) => (AVATAR_PALETTE.find((c) => c.id === id) || AVATAR_PALETTE[0]).hex;

export default function UserSettingsModal({ open, onClose }) {
    const { user, refresh: refreshAuth } = useAuth();
    const [tab, setTab] = useState("profile");
    const [name, setName] = useState(user?.name || "");
    const [jobTitle, setJobTitle] = useState(user?.job_title || "");
    const [avatarColor, setAvatarColor] = useState(user?.avatar_color || "signal");
    const [busy, setBusy] = useState(false);
    const [permissions, setPermissions] = useState({ role: user?.role, permissions: [], bypass_all: false });
    const [permsLoading, setPermsLoading] = useState(false);

    // Password tab state
    const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
    const [pwdErr, setPwdErr] = useState("");

    useEffect(() => {
        if (!open || !user) return;
        setName(user.name || "");
        setJobTitle(user.job_title || "");
        setAvatarColor(user.avatar_color || "signal");
        setPwd({ current: "", next: "", confirm: "" });
        setPwdErr("");
    }, [open, user]);

    useEffect(() => {
        if (!open || tab !== "credentials") return;
        setPermsLoading(true);
        api.get("/users/me/permissions")
            .then((r) => setPermissions(r.data))
            .catch(() => setPermissions({ role: user?.role, permissions: [], bypass_all: false }))
            .finally(() => setPermsLoading(false));
    }, [open, tab, user]);

    if (!open) return null;

    const dirty =
        name.trim() !== (user?.name || "") ||
        jobTitle.trim() !== (user?.job_title || "") ||
        avatarColor !== (user?.avatar_color || "signal");

    const saveProfile = async () => {
        setBusy(true);
        try {
            await api.patch("/users/me", {
                name: name.trim(),
                job_title: jobTitle.trim(),
                avatar_color: avatarColor,
            });
            await refreshAuth();
            toast.success("Profile updated");
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err?.message);
        } finally { setBusy(false); }
    };

    const submitPassword = async (e) => {
        e.preventDefault();
        setPwdErr("");
        if (pwd.next !== pwd.confirm) {
            setPwdErr("New password and confirmation do not match.");
            return;
        }
        if (pwd.next.length < 8) {
            setPwdErr("New password must be at least 8 characters.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/users/me/change-password", {
                current_password: pwd.current,
                new_password: pwd.next,
                confirm_password: pwd.confirm,
            });
            toast.success("Password changed");
            setPwd({ current: "", next: "", confirm: "" });
        } catch (err) {
            setPwdErr(formatApiErrorDetail(err?.response?.data?.detail) || err?.message);
        } finally { setBusy(false); }
    };

    const TABS = [
        { id: "profile", label: "Profile", icon: UserIcon },
        { id: "security", label: "Security", icon: Lock },
        { id: "credentials", label: "Credentials", icon: ShieldCheck },
    ];

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-jet/85 backdrop-blur-sm p-4"
            data-testid="user-settings-modal"
            onClick={onClose}
        >
            <div
                className="relative w-full max-w-2xl bg-jet border border-hairline max-h-[90vh] overflow-y-auto shadow-[0_0_60px_rgba(255,106,0,0.10)]"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between border-b border-hairline px-6 py-4">
                    <div className="flex items-center gap-3">
                        <Avatar user={{ ...user, name, avatar_color: avatarColor }} size={44} />
                        <div>
                            <div className="overline">User Profile</div>
                            <div className="heading-display text-lg leading-tight">{name || user?.email}</div>
                            <div className="text-[11px] text-snow-muted font-mono mt-0.5">{user?.email}</div>
                        </div>
                    </div>
                    <button
                        data-testid="user-settings-close-btn"
                        onClick={onClose}
                        className="text-snow-dim hover:text-signal transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-hairline" data-testid="user-settings-tabs">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            data-testid={`user-settings-tab-${t.id}`}
                            onClick={() => setTab(t.id)}
                            className={`flex items-center gap-2 px-5 py-3 text-[11px] font-mono uppercase tracking-[0.18em] border-b-2 transition-colors ${
                                tab === t.id
                                    ? "border-signal text-signal"
                                    : "border-transparent text-snow-dim hover:text-snow"
                            }`}
                        >
                            <t.icon className="w-3.5 h-3.5" /> {t.label}
                        </button>
                    ))}
                </div>

                {/* Tab body */}
                <div className="p-6">
                    {tab === "profile" && (
                        <div className="space-y-5" data-testid="user-settings-profile">
                            <Field label="Full Name">
                                <input
                                    data-testid="profile-name-input"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="cerb-input"
                                    minLength={2}
                                    maxLength={120}
                                />
                            </Field>
                            <Field label="Job Title">
                                <input
                                    data-testid="profile-job-title-input"
                                    value={jobTitle}
                                    onChange={(e) => setJobTitle(e.target.value)}
                                    className="cerb-input"
                                    maxLength={120}
                                    placeholder="e.g. CEO · Senior Tech · Floor Supervisor"
                                />
                            </Field>
                            <Field label="Avatar Theme Color" hint={<Palette className="inline w-3 h-3 mr-1" />}>
                                <div className="flex items-center gap-3 flex-wrap" data-testid="profile-avatar-palette">
                                    {AVATAR_PALETTE.map((c) => (
                                        <button
                                            type="button"
                                            key={c.id}
                                            data-testid={`avatar-color-${c.id}`}
                                            onClick={() => setAvatarColor(c.id)}
                                            className={`group flex flex-col items-center gap-1.5 transition-transform hover:-translate-y-0.5 ${
                                                avatarColor === c.id ? "scale-110" : ""
                                            }`}
                                            title={c.label}
                                        >
                                            <span
                                                className={`block w-7 h-7 rounded-full border-2 transition-colors ${
                                                    avatarColor === c.id ? "border-signal" : "border-[#2A2A2A]"
                                                }`}
                                                style={{ background: c.hex }}
                                            />
                                            <span className="text-[9px] font-mono uppercase tracking-wider text-snow-dim group-hover:text-snow">
                                                {c.label}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </Field>
                            <div className="flex justify-end gap-3 pt-3 border-t border-hairline">
                                <button onClick={onClose} className="cerb-btn-secondary" data-testid="profile-cancel-btn">Close</button>
                                <button
                                    onClick={saveProfile}
                                    disabled={!dirty || busy || name.trim().length < 2}
                                    className="cerb-btn-primary"
                                    data-testid="profile-save-btn"
                                >
                                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> Save Profile</>}
                                </button>
                            </div>
                        </div>
                    )}

                    {tab === "security" && (
                        <form onSubmit={submitPassword} className="space-y-5" data-testid="user-settings-security">
                            <div className="border border-signal bg-signal/5 px-3 py-2.5 text-[11px] font-mono text-signal flex items-start gap-2">
                                <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                <div>
                                    <div className="font-bold uppercase tracking-wider">Change Password</div>
                                    <div className="text-snow-dim mt-0.5">
                                        Your current password is required. New password must be at least 8 characters
                                        and different from the current one.
                                    </div>
                                </div>
                            </div>
                            <Field label="Current Password">
                                <input
                                    data-testid="password-current-input"
                                    type="password"
                                    required
                                    value={pwd.current}
                                    onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
                                    className="cerb-input"
                                />
                            </Field>
                            <Field label="New Password">
                                <input
                                    data-testid="password-new-input"
                                    type="password"
                                    required
                                    minLength={8}
                                    value={pwd.next}
                                    onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
                                    className="cerb-input"
                                />
                            </Field>
                            <Field label="Confirm New Password">
                                <input
                                    data-testid="password-confirm-input"
                                    type="password"
                                    required
                                    minLength={8}
                                    value={pwd.confirm}
                                    onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                                    className="cerb-input"
                                />
                            </Field>
                            {pwdErr && (
                                <div className="border border-red-500/50 bg-red-500/5 text-red-400 px-3 py-2 text-xs font-mono" data-testid="password-error">
                                    {pwdErr}
                                </div>
                            )}
                            <div className="flex justify-end gap-3 pt-3 border-t border-hairline">
                                <button type="button" onClick={onClose} className="cerb-btn-secondary">Close</button>
                                <button
                                    type="submit"
                                    disabled={busy || !pwd.current || !pwd.next || !pwd.confirm}
                                    className="cerb-btn-primary"
                                    data-testid="password-submit-btn"
                                >
                                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Lock className="w-4 h-4" /> Change Password</>}
                                </button>
                            </div>
                        </form>
                    )}

                    {tab === "credentials" && (
                        <div className="space-y-5" data-testid="user-settings-credentials">
                            <div>
                                <div className="overline mb-2">System Credentials</div>
                                <div className="border border-[#2A2A2A] bg-jet-elev1 p-4 space-y-3">
                                    <Row k="Email" v={user?.email} mono />
                                    <Row k="KITT Role" v={
                                        <span className="inline-flex items-center gap-2">
                                            <span className="font-mono text-snow">{ROLE_LABELS[permissions.role] || permissions.role || "—"}</span>
                                            {permissions.bypass_all && (
                                                <span className="inline-flex items-center border bg-signal text-jet font-bold px-2 py-0.5 text-[9px] uppercase tracking-[0.2em]">
                                                    Bypass All
                                                </span>
                                            )}
                                        </span>
                                    } />
                                    <Row k="Status" v={<StatusPill status={user?.status} />} />
                                </div>
                            </div>

                            <div>
                                <div className="overline mb-2">Active Permissions ({permissions.permissions?.length || 0})</div>
                                {permsLoading ? (
                                    <div className="text-xs font-mono text-snow-dim flex items-center gap-2">
                                        <Loader2 className="w-3 h-3 animate-spin text-signal" /> Loading permissions…
                                    </div>
                                ) : (
                                    <div
                                        className="border border-[#2A2A2A] bg-jet-elev1 p-3 grid grid-cols-2 gap-1.5 max-h-72 overflow-y-auto"
                                        data-testid="credentials-permissions-list"
                                    >
                                        {(permissions.permissions || []).map((p) => (
                                            <div
                                                key={p}
                                                data-testid={`perm-row-${p}`}
                                                className="flex items-center gap-2 text-[11px] font-mono text-snow-dim"
                                            >
                                                <Check className="w-3 h-3 text-signal flex-shrink-0" />
                                                <span>{p}</span>
                                            </div>
                                        ))}
                                        {(permissions.permissions || []).length === 0 && (
                                            <div className="text-snow-muted font-mono text-xs col-span-2 py-3 text-center">
                                                No permissions assigned.
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <div>
            <label className="overline block mb-2">{hint}{label}</label>
            {children}
        </div>
    );
}
function Row({ k, v, mono }) {
    return (
        <div className="flex items-center justify-between gap-4 text-sm">
            <div className="text-xs font-mono uppercase tracking-wider text-snow-muted">{k}</div>
            <div className={`text-snow ${mono ? "font-mono" : ""}`}>{v}</div>
        </div>
    );
}
function StatusPill({ status }) {
    const cls = {
        Active: "border-signal text-signal",
        Pending: "border-snow-muted text-snow-muted",
        Suspended: "border-red-500/40 text-red-400",
        Inactive: "border-red-500/40 text-red-400",
    }[status] || "border-hairline text-snow-dim";
    return <span className={`inline-flex items-center border px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-mono ${cls}`}>{status}</span>;
}

/** Reusable sidebar/header avatar — initials inside a tinted ring. */
export function Avatar({ user, size = 36, testId = "user-avatar" }) {
    if (!user) return null;
    const initials = (user.name || user.email || "?")
        .split(/[ .@]/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0]?.toUpperCase() || "")
        .join("");
    const color = avatarHex(user.avatar_color || "signal");
    return (
        <div
            data-testid={testId}
            className="relative flex items-center justify-center flex-shrink-0"
            style={{ width: size, height: size }}
            title={user.name || user.email}
        >
            <div
                className="absolute inset-0 rounded-full"
                style={{ boxShadow: `0 0 0 2px ${color}, inset 0 0 8px ${color}33`, background: "#0e0e0e" }}
            />
            <span
                className="relative font-mono font-bold tracking-wide"
                style={{ color, fontSize: size * 0.34 }}
            >
                {initials}
            </span>
        </div>
    );
}
